import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { HttpClient } from 'apiclient/ApiClient';

export interface UseReportDeletionConfig {
    apiEndpoint: string; // e.g., 'reports' or 'organization-reports'
    queryKeyToInvalidate: string;
}

export interface UseReportDeletionReturn {
    // State
    isDeleting: boolean;
    deleteDialogOpen: boolean;
    reportToDelete: string;
    
    // Actions
    handleDeleteClick: (reportId: string) => void;
    handleDeleteConfirm: () => void;
    handleDeleteCancel: () => void;
}

export function useReportDeletion(config: UseReportDeletionConfig): UseReportDeletionReturn {
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [reportToDelete, setReportToDelete] = React.useState<string>('');
    
    const httpClient = HttpClient();
    const queryClient = useQueryClient();
    
    const { apiEndpoint, queryKeyToInvalidate } = config;

    // Delete report mutation with optimistic updates
    const reportDeleteMutation = useMutation(
        async (reportId: string) => {
            await httpClient.delete(`${apiEndpoint}/${reportId}`);
        },
        {
            onMutate: async (reportId: string) => {
                // Cancel any outgoing refetches
                await queryClient.cancelQueries(queryKeyToInvalidate);

                // Snapshot the previous value
                const previousReports = queryClient.getQueryData(queryKeyToInvalidate);

                // Optimistically update to the new value
                queryClient.setQueryData(queryKeyToInvalidate, (old: any) => {
                    if (!old) return old;
                    return old.filter((report: any) => report.report_id !== reportId);
                });

                // Return a context object with the snapshotted value
                return { previousReports };
            },
            onError: (err, reportId, context: any) => {
                // If the mutation fails, use the context to roll back
                queryClient.setQueryData(queryKeyToInvalidate, context?.previousReports);
            },
            onSettled: () => {
                // Always refetch after error or success to ensure server state
                queryClient.invalidateQueries(queryKeyToInvalidate);
            }
        }
    );

    const handleDeleteClick = React.useCallback((reportId: string) => {
        setReportToDelete(reportId);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = React.useCallback(() => {
        if (reportToDelete) {
            reportDeleteMutation.mutate(reportToDelete);
            setDeleteDialogOpen(false);
            setReportToDelete('');
        }
    }, [reportToDelete, reportDeleteMutation]);

    const handleDeleteCancel = React.useCallback(() => {
        setDeleteDialogOpen(false);
        setReportToDelete('');
    }, []);

    return {
        // State
        isDeleting: reportDeleteMutation.isLoading,
        deleteDialogOpen,
        reportToDelete,
        
        // Actions
        handleDeleteClick,
        handleDeleteConfirm,
        handleDeleteCancel
    };
}